import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import OpenInFull from "@mui/icons-material/OpenInFull";
import Refresh from "@mui/icons-material/Refresh";
import Star from "@mui/icons-material/Star";
import StarBorder from "@mui/icons-material/StarBorder";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import classNames from "classnames";
import { useCallback } from "react";
import { useNavigate } from "react-router";
import { BotChat } from "../../interfaces/sirius";
import { Link as ToolpadLink } from "../Common/Link";
import { useStarChat } from "../Sirius/SiriusQueries";
import DeleteChatDialog from "../useDialogs/DeleteChatDialog";
import RenameChatDialog from "../useDialogs/RenameChatDialog";
import { useDialogs } from "../useDialogs/useDialogs";
import { getItemTitle } from "../useNavigation/navigation";

const PageContentHeader = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  gap: theme.spacing(2),
}));

function ChatTitleToolbar(chat: BotChat, isIframe: boolean) {
  const dialogs = useDialogs();
  const { mutate: starMutate } = useStarChat(chat.bot.id);
  const navigate = useNavigate();
  const handleFavorite = () => {
    starMutate({ chatId: chat.id, isFavorite: !chat.favourite });
  };

  if (isIframe) {
    return (
      <div className="flex justify-end">
        <IconButton
          onClick={() => navigate(`/sirius/bot/${chat.bot.id}/chat/new`)}
          title="Rafraîchir"
        >
          <Refresh />
        </IconButton>
        <IconButton
          onClick={() => window.open(window.location.href, "_blank")}
          title="Ouvrir dans une nouvelle fenêtre"
        >
          <OpenInFull />
        </IconButton>
      </div>
    );
  }

  return (
    <div className="flex items-center">
      <Tooltip
        title={chat.favourite ? "Retirer des favoris" : "Ajouter aux favoris"}
      >
        <IconButton onClick={handleFavorite}>
          {chat.favourite ? <Star sx={{ color: "gold" }} /> : <StarBorder />}
        </IconButton>
      </Tooltip>
      <Tooltip title="Renommer">
        <IconButton
          onClick={async () => {
            await dialogs.open(RenameChatDialog, chat);
          }}
        >
          <Edit />
        </IconButton>
      </Tooltip>
      <Tooltip title="Supprimer">
        <IconButton
          onClick={async () => {
            await dialogs.open(DeleteChatDialog, chat);
          }}
        >
          <Delete />
        </IconButton>
      </Tooltip>
    </div>
  );
}

function ChatTitle({
  chat,
  isIframe,
}: {
  chat: BotChat | undefined;
  isIframe: boolean;
}) {
  const PageToolbarComponent = useCallback(
    () => chat && ChatTitleToolbar(chat, isIframe),
    [chat, isIframe]
  );

  const breadcrumbs =
    chat && chat.bot.name
      ? [
          { title: chat.bot.name, path: `/sirius/bot/${chat.bot.id}` },
          {
            title: chat.title ?? "Sans titre",
            path: `/sirius/chat/${chat.id}`,
          },
        ]
      : [];

  return (
    <Stack className="px-6 mt-4">
      {!isIframe && (
        <Breadcrumbs aria-label="breadcrumb">
          {breadcrumbs
            ? breadcrumbs.map((item, index) => {
                return item.path ? (
                  <Link
                    key={index}
                    component={ToolpadLink}
                    underline="hover"
                    color="inherit"
                    href={item.path}
                  >
                    {getItemTitle(item)}
                  </Link>
                ) : (
                  <Typography key={index} color="text.primary">
                    {getItemTitle(item)}
                  </Typography>
                );
              })
            : null}
        </Breadcrumbs>
      )}

      <PageContentHeader className={classNames({ "justify-end": isIframe })}>
        {!isIframe && chat?.title && (
          <Typography variant="h4">{chat.title}</Typography>
        )}
        <PageToolbarComponent />
      </PageContentHeader>
    </Stack>
  );
}

export default ChatTitle;
